import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Lock, Pencil, Users, Store } from 'lucide-react';
import { erpList } from '../../../services/erpApi';
import { Card, Button, PageHeader, Table, EmptyState, Badge, SearchBox, fmtInr, fmtDate } from '../ui';
import { PartyForm } from './PartyForm';

const CFG = {
  customers: { label: 'Customers', singular: 'Customer', icon: Users, prefix: 'CUST' },
  vendors: { label: 'Vendors', singular: 'Vendor', icon: Store, prefix: 'VEND' },
};

export const Parties = ({ kind }) => {
  const cfg = CFG[kind];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(null);
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await erpList(kind, q ? { q } : undefined); setRows(r.data.items || []); setCanView(r.data.can_view_identity); }
    catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [kind, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const open = (r) => setModal(r || {});

  const columns = [
    { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs font-semibold px-2 py-1 rounded-md" style={{ background: '#f3f6fb' }}>{r.code}</span> },
    { key: 'name', label: cfg.singular, render: (r) => (
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-full grid place-items-center text-xs font-bold text-white flex-shrink-0" style={{ background: r.identity_hidden ? '#9aa3ba' : 'var(--erp-navy)' }}>{r.identity_hidden ? <Lock size={12} /> : (r.name || '?')[0]}</span>
        <div><p className="font-semibold">{r.name}</p>{canView && (r.segment || r.email) && <p className="text-xs" style={{ color: 'var(--erp-muted)' }}>{[r.segment, r.email].filter(Boolean).join(' · ')}</p>}</div>
      </div>) },
    { key: 'place', label: 'City / State', render: (r) => <span>{r.city ? `${r.city}, ` : ''}{r.state || '—'}</span> },
    ...(canView ? [
      { key: 'gstin', label: 'GSTIN', render: (r) => r.gstin ? <span className="font-mono text-xs">{r.gstin}</span> : <Badge tone="amber">Missing</Badge> },
      { key: 'contact', label: 'Contact', render: (r) => <div className="text-xs"><p className="font-semibold">{r.contact || '—'}</p><p style={{ color: 'var(--erp-muted)' }}>{r.phone || r.email || ''}</p></div> },
      { key: 'credit_limit', label: 'Credit', align: 'right', render: (r) => <div className="text-xs"><p className="font-bold">{r.credit_limit ? fmtInr(r.credit_limit) : '—'}</p><p style={{ color: 'var(--erp-muted)' }}>{r.credit_days ? `${r.credit_days} days` : ''}</p></div> },
      ...(kind === 'customers' ? [{ key: 'potential_total', label: 'Potential / yr', align: 'right', render: (r) => r.potential_total ? <span className="font-semibold" style={{ color: 'var(--erp-blue)' }}>{fmtInr(r.potential_total)}</span> : '—' }] : []),
    ] : []),
    { key: 'created_at', label: 'Since', render: (r) => <span style={{ color: 'var(--erp-muted)' }}>{fmtDate(r.created_at)}</span> },
    ...(canView ? [{ key: 'edit', label: '', align: 'right', render: (r) => <Button variant="ghost" size="sm" icon={Pencil} onClick={(e) => { e.stopPropagation(); open(r); }} data-testid={`party-edit-${r.id}`} /> }] : []),
  ];

  return (
    <div data-testid={`erp-${kind}`}>
      <PageHeader crumb="Partners" title={cfg.label} subtitle={canView ? `Manage ${cfg.label.toLowerCase()}, GST details and credit terms.` : `Identity protected — you operate on ${cfg.singular.toLowerCase()} codes only.`}
        actions={canView && <Button icon={Plus} onClick={() => open(null)} data-testid="party-add">New {cfg.singular}</Button>} />
      {canView === false && (
        <div className="flex items-center gap-2.5 text-sm rounded-xl px-4 py-3 mb-4 erp-rise" style={{ background: '#fff4e0', color: '#8a4b00' }} data-testid="identity-banner">
          <Lock size={15} /> Real names and contact details are hidden by your role. Codes like <b>{cfg.prefix}00001</b> identify each party.
        </div>
      )}
      <Card className="p-0 erp-rise erp-d1">
        <div className="flex flex-wrap items-center gap-3 px-5 pt-5 pb-3">
          <p className="text-sm font-semibold flex-1" style={{ color: 'var(--erp-muted)' }}>{rows.length} {cfg.label.toLowerCase()}</p>
          <SearchBox value={q} onChange={setQ} placeholder={`Search ${canView ? 'name or code' : 'code'}…`} className="w-72" data-testid="party-search" />
        </div>
        <Table testid="party-table" columns={columns} rows={rows} loading={loading} onRowClick={canView ? open : undefined}
          empty={<EmptyState icon={cfg.icon} title={`No ${cfg.label.toLowerCase()} yet`} text={canView ? `Add your first ${cfg.singular.toLowerCase()} to start transacting.` : ''} action={canView && <Button icon={Plus} onClick={() => open(null)}>New {cfg.singular}</Button>} />} />
      </Card>

      {modal && <PartyForm kind={kind} initial={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
};
